import Tone from 'tone';
import {PATTERN_SWITCH} from 'session/constants.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import TrackVolumeChangeEvent from 'session/TrackVolumeChangeEvent.js';
import {TRACK_VOLUME_CHANGE} from 'session/constants.js';
import GroupVolumeChangeEvent from 'session/GroupVolumeChangeEvent.js';
import {GROUP_VOLUME_CHANGE} from 'session/constants.js';
import TrackMuteEvent from 'session/TrackMuteEvent.js';
import {TRACK_MUTE} from 'session/constants.js';
import GroupMuteEvent from 'session/GroupMuteEvent.js';
import {GROUP_MUTE} from 'session/constants.js';
import ParameterChangeEvent from 'session/ParameterChangeEvent.js';
import {PARAMETER_CHANGE} from 'session/constants.js';
import TrackSendVolumeChangeEvent from 'session/TrackSendVolumeChangeEvent.js';
import {TRACK_SEND_VOLUME_CHANGE} from 'session/constants.js';
import TrackSoloEvent from 'session/TrackSoloEvent.js';
import {TRACK_SOLO} from 'session/constants.js';
import TrackReverseEvent from 'session/TrackReverseEvent.js';
import {TRACK_REVERSE} from 'session/constants.js';
import {
    RESOLUTION_CHANGE,
    SEQUENCE_ROLL,
    SUB_SEQUENCE_VOLUME_CHANGE,
    LINEAR_PARAMETER_CHANGE
} from 'session/constants.js';
import MixByType from './MixByType.js';
import LinearMixByType from './LinearMixByType.js';
import DeterministicMixByType from './DeterministicMixByType.js';
import ResolutionChangeEvent from './ResolutionChangeEvent.js';
import SequenceRollEvent from './SequenceRollEvent.js';
import Resolution from 'engine/Resolution.js';
import {dispatch} from './operations/dispatch.js';
import AddEvent from './operations/AddEvent.js';
import DeleteEvent from './operations/DeleteEvent.js';
import DeleteCompoundEvent from './operations/DeleteCompoundEvent.js';
import AddDeterministicEvent from './operations/AddDeterministicEvent.js';
import InsertEvent from './operations/InsertEvent.js';
import ResizeEvent from './operations/ResizeEvent.js';
import PasteEvents from './operations/PasteEvents.js';
import ResizeSequenceRoll from './operations/ResizeSequenceRoll.js';
import AbstractMix from './AbstractMix.js';
import {getTotalLength} from "components/arrangement/getTotalLength.js";
import {
    setMixEvents,
    setTotalArrangementLength
} from 'actions/index.js';
// import ParametersAutomation from './parameters/ParametersAutomation.js';
import SubSequenceVolumeChangeEvent from './SubSequenceVolumeChangeEvent.js';

class Mix extends AbstractMix {
    constructor(sequencerEngine, subSequence) {
        super(sequencerEngine, subSequence);
        this.mixesByType = {};

        // linear mix types
        this.mixesByType[SUB_SEQUENCE_VOLUME_CHANGE] =
            new LinearMixByType(SUB_SEQUENCE_VOLUME_CHANGE, sequencerEngine, subSequence);

        /*
        this.mixesByType[LINEAR_PARAMETER_CHANGE] =
            new LinearMixByParameter(LINEAR_PARAMETER_CHANGE, sequencerEngine, subSequence);
            */
       
        // regular mix types
        this.deterministicMixesByType = {};
        this.deterministicMixesByType[RESOLUTION_CHANGE]  =
            new DeterministicMixByType(
                RESOLUTION_CHANGE,
                new ResolutionChangeEvent(new Resolution()),
                this.subSequence);
        this.deterministicMixesByType[SEQUENCE_ROLL] =
            new DeterministicMixByType(
                SEQUENCE_ROLL,
                new SequenceRollEvent(false, new Resolution()),
                this.subSequence);
        this.deterministicMixesByType[PATTERN_SWITCH] =
            new DeterministicMixByType(
                PATTERN_SWITCH,
                new PatternSwitchEvent(0),
                this.subSequence);

        if (subSequence) {
            this.updateAllTypeRedux();
        }
    }

    getValue(type, step) {
        let mixByType = this.deterministicMixesByType[type];
        if (mixByType === undefined) {
            return undefined;
        }

        return mixByType.getValue(step, true);
    }

    getEvent(type, step) {
        let mixByType = this.deterministicMixesByType[type];
        if (mixByType === undefined) {
            return undefined;
        }

        return mixByType.getEvent(step);
    }

    getValuesBetweenSteps(type, step1, step2) {
        let mixByType = this.deterministicMixesByType[type];
        if (mixByType === undefined) {
            return undefined;
        }

        return mixByType.getValuesBetweenSteps(step1, step2);
    }

    holdEvent(event) {
        if (!event.isDeterministic()) {
            return;
        }

        let type = event.getType();
        if (! type in this.deterministicMixesByType) {
            this.deterministicMixesByType[type] = new DeterministicMixByType();
        }
        this.deterministicMixesByType[type].holdEvent(event);
    }

    dispatch(action) {
        dispatch(this, action);
    }

    addEvent(event) {
        let timing = this.getTimeForEventToAdd(event);
        if (timing === undefined) {
            return;
        }

        // set the sub sequence
        event.subSequence = this.subSequence;

        let mix = undefined;

        // finally we add the event to the mix
        event.cancel();
        if (event.isDeterministic()) {
            new AddDeterministicEvent(this, event, timing.time, timing.step)
                .execute();
        } else {
            new AddEvent(this, event, timing.time, timing.step)
                .execute();
        }

        setTimeout(() => {
            event.canceled = false;
        }, 5000);

        if (event.isDeterministic()) {
            this.updateAllTypeRedux();
        }
    }

    getNextEvent(event) {
        let type = event.getType();
        return this.deterministicMixesByType[type].getNextEvent(event);
    }

    getPreviousEvent(event) {
        let type = event.getType();
        return this.deterministicMixesByType[type].getPreviousEvent(event);
    }

    resizeEvent(event, newSize, inPlace=false) {
        new ResizeEvent(this, event, newSize, inPlace)
            .execute();
        this.updateAllTypeRedux();
    }

    resizeSequenceRoll(rollEvent, resolutionEvent, direction, newSize) {
        new ResizeSequenceRoll(
            this,
            rollEvent,
            resolutionEvent,
            direction,
            newSize)
            .execute();
        this.updateAllTypeRedux();
    }

    insertEvent(event, duration) {
        new InsertEvent(this, event, duration)
            .execute();
        this.updateAllTypeRedux();
    }

    pasteEvents(events, position) {
        new PasteEvents(this, events, position)
            .execute();
        this.updateAllTypeRedux();
    }

    deleteEvent(event, inPlace) {
        new DeleteEvent(this, event, inPlace)
            .execute();
        this.updateAllTypeRedux();
    }

    deleteCompoundEvent(event, subEvent) {
        new DeleteCompoundEvent(this, event, subEvent)
            .execute();
        this.updateAllTypeRedux();
    }

    seekToTrackStep(trackStep) {
        for (let type in this.mixesByType) {
            this.mixesByType[type].seekToTrackStep(trackStep);
        }

        for (let type in this.deterministicMixesByType) {
            this.deterministicMixesByType[type].seekToTrackStep(trackStep);
        }
    }

    restart(isLooping=false) {
        for (let type in this.mixesByType) {
            this.mixesByType[type].restart(isLooping);
        }

        for (let type in this.deterministicMixesByType) {
            this.deterministicMixesByType[type].restart(isLooping);
        }
    }

    async scheduleNext(step, time) {
        let currentTime = new Tone.Time(Tone.Master.context.currentTime)
            .toMilliseconds();

        // calculate how many milliseconds until this 'time' arrives (i.e.
        // the time the step will hit)
        let timeUntilEvent = new Tone.Time(time).toMilliseconds() - currentTime;

        for (let type in this.mixesByType) {
            this.mixesByType[type].scheduleNext(step, timeUntilEvent, this.sequencerEngine);
        }
    }

    eraseEventsAtStep(step) {
        for (let type in this.mixesByType) {
            this.mixesByType[type].eraseEventsAtStep(step);
        }
    }

    schedule() {
        for (let type in this.mixesByType) {
            this.mixesByType[type].schedule();
        }
    }

    getJson() {
        let mixesByType = {};
        for (let type in this.mixesByType) {
            mixesByType[type] = this.mixesByType[type].getJson();
        }

        let deterministicMixesByType = {};
        for (let type in this.deterministicMixesByType) {
            deterministicMixesByType[type] = this.deterministicMixesByType[type].getJson();
        }
        return {
            name: this.name,
            mixesByType: mixesByType,
            deterministicMixesByType: deterministicMixesByType
        };
    }

    getCopy() {
        let mix = new Mix(this.name);
        mix.mixesByType = this.mixesByType;
        mix.deterministicMixesByType = this.deterministicMixesByType;
        return mix;
    }

    getFirstEvent(type) {
        let m = this.deterministicMixesByType[type];
        return m.defaultValue;
    }

    loadFromJson(json, sequencerEngine) {
        if (json === undefined) {
            return;
        }

        sequencerEngine = this.sequencerEngine;

        for (let type in json.mixesByType) {
            this.mixesByType[type] = new LinearMixByType(
                parseInt(type),
                sequencerEngine,
                this.subSequence);
            this.mixesByType[type].loadFromJson(
                json.mixesByType[type],
                sequencerEngine,
                this.subSequence);
        }

        for (let type in json.deterministicMixesByType) {
            type = parseInt(type);
            let holdValue = undefined;
            if (type === PATTERN_SWITCH) {
                holdValue = new PatternSwitchEvent(0);
            } else if (type === SEQUENCE_ROLL) {
                holdValue = new SequenceRollEvent(false, new Resolution());
            } else if (type === RESOLUTION_CHANGE) {
                holdValue = new ResolutionChangeEvent(new Resolution());
            }

            this.deterministicMixesByType[type] =
                new DeterministicMixByType(parseInt(type), holdValue);
            this.deterministicMixesByType[type]
                .loadFromJson(json.deterministicMixesByType[type], sequencerEngine);
        }
 
        this.updateAllTypeRedux();
    }

    updateTypeRedux(type, reduxCall=setMixEvents) {
        // put the pattern switch events of current mix into redux
        let mix = this.deterministicMixesByType[type];

        // calculate the duration of each pattern
        let events = [];
        let event = this.getFirstEvent(type);
        event.subSequence = this.subSequence;
        event.step = 0;
        for (let i=0; i < mix.events.length; i++) {
            event.subSequence = this.subSequence;
            event.duration = mix.events[i].step - event.step;
            events.push(event);
            event = mix.events[i];
        }
        
        let lastEvent = mix.events[mix.events.length - 1] ||
            event;
        if (lastEvent) {
            lastEvent.duration = undefined;
            lastEvent.subSequence = this.subSequence;
            events.push(lastEvent);
        }
        
        this.sequencerEngine.store.dispatch(reduxCall(
            this.subSequence.id, type, events));
    }

    updateAllTypeRedux() {
        for (let type in this.mixesByType) {
            this.mixesByType[type].updateRedux(this.subSequence, this.sequencerEngine);
        }

        this.updateTypeRedux(
            PATTERN_SWITCH,
            setMixEvents);

        this.updateTypeRedux(
            SEQUENCE_ROLL,
            setMixEvents);

        // update the total length in redux for the arr. view
        this.sequencerEngine.dispatch(
            setTotalArrangementLength(getTotalLength(this.sequencerEngine)));
    }

    clear() {
        for (let type in this.deterministicMixesByType) {
            this.deterministicMixesByType[type].clear();
        }
        for (let type in this.mixesByType) {
            this.mixesByType[type].clear();
        }

        this.updateAllTypeRedux();
    }

    get length() {
        let steps = [];
        for (let type in this.deterministicMixesByType) {
            let mix = this.deterministicMixesByType[type];
            steps = [... steps, ...mix.events.map(x => x.step)];
        }
        for (let type in this.mixesByType) {
            let mix = this.mixesByType[type];
            steps = [... steps, ... mix.events.map(x => x.step)];
        }
        let max = Math.max(... steps.filter(x => x !== undefined && !isNaN(x)));
        return max === -Infinity ? 0 : max;
    }

    prune(type) {
        if (type in this.deterministicMixesByType) {
            this.deterministicMixesByType[type].prune();
        }

        if (type in this.mixesByType) {
            this.deterministicMixesByType[type].prune();
        }
    }
}

export default Mix;
